import { useCallback, useMemo } from 'react';

import { useIsFocused } from '@react-navigation/native';

import { EventInfo } from '@/api/events/types/types';
import { usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerProjection, PlayerWithTeam, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { CardSegmentTitleType } from '@/feature/fantasy-event-sheets/components/ufc-events/CardSegmentTitle';
import { LeaguePositionFilters } from '@/hooks/use-fantasy-league-configs';
import { CardSegment, EventAttributesType, IndividualVersusEvent } from '@/types/api.generated';
import { findEventAttribute } from '@/utils/fantasy-attribute-utils';
import { gameUtils } from '@/utils/games';
import { sortEventsByDate } from '@/utils/sortEvents';
import { sortPlayersByAwayTeam, sortPlayersByEventStartDate, sortPlayersByPromoPicks } from '@/utils/sortPlayers';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { ALL_POSITIONS, FilterType, Filters } from '../utils/filters';
import { checkIfPromoProjection } from '../utils/lineupsUtils';
import { POPULAR_PLAYERS_FILTER } from '../utils/players';
import { filterByPositionFunction, usePlayerFiltersState } from './use-player-filters';
import { usePopularPlayersData } from './use-popular-players-data';

const UFC_CARD_SEGMENT_ORDER = [CardSegment.MainCard, CardSegment.Prelims, CardSegment.EarlyPrelims];

/**
 * Hook that sorts and filters players based on the active filter tab (Position or Projection).
 * Also computes the popular players list used by the Projection tab's Popular filter.
 *
 * @param sortedEvents - Pre-sorted list of events to derive players from.
 * @param positionFilters - Position filters configured for the current league.
 * @param filterType - Whether the active tab is "Position" or "Projection", controls which sorted list is returned.
 * @returns `sortedPlayers` - the filtered/sorted player list for the active tab;
 *          `popularProjectionsPlayers` - players from `usePopularPlayers`, used by the Projection tab's Popular chip.
 */
export function useSortedListOfPlayers({
    sortedEvents,
    positionFilters,
    filterType,
}: {
    sortedEvents: EventInfo[];
    positionFilters: LeaguePositionFilters;
    filterType: FilterType;
}): { sortedPlayers: PlayerWithTeamAndEvent[]; popularProjectionsPlayers: PlayerWithTeam[] | undefined } {
    const popularProjectionsPlayers = usePopularProjectionsPlayers(sortedEvents);

    const { playerPositionFilter, playerProjectionFilter } = usePlayerFiltersState();

    const isFocused = useIsFocused();

    // skip subscription-triggered re-renders while the screen is not focused (fantasy sheets are
    // open on top). there is no risk of stale data, since isFocused will trigger a re-render
    // with the latest store data when the screen is refocused
    const skipWhenUnfocused = useCallback(
        (prev: Record<string, PlayerProjection[]>, next: Record<string, PlayerProjection[]>) =>
            !isFocused || Object.is(prev, next),
        [isFocused]
    );
    const allMarkets = useStoreWithEqualityFn(usePlayerPropsStore, state => state._playerMarkets, skipWhenUnfocused);

    // Normalize undefined to the default first position filter so that the initial computation
    // (on mount while Projection tab is active) matches the value after the filter is set,
    // avoiding a recompute on the first tab switch to Position
    const effectivePositionFilter = playerPositionFilter ?? positionFilters?.[0]?.position ?? ALL_POSITIONS;

    // Sorting across all events is expensive, so we do it once for all positions.
    // Position-specific lists are derived below via a cheap filter on this result,
    // so tapping a position chip never re-triggers the sort.
    const allPositionSortedPlayers: PlayerWithTeam[] = useMemo(() => {
        if (sortedEvents.length === 0) return [];
        const players = sortedEvents.flatMap(event =>
            gameUtils.filterAndSortPlayers(event, positionFilters, ALL_POSITIONS)
        );
        return players.sort(sortPlayersByPromoPicks);
    }, [sortedEvents, positionFilters]);

    // Filtering is cheap: we derive from the sorted list above so position changes
    // never re-trigger the sort.
    const positionSortedPlayers: PlayerWithTeam[] = useMemo(() => {
        if (effectivePositionFilter === ALL_POSITIONS) {
            return allPositionSortedPlayers;
        }
        return allPositionSortedPlayers.filter(filterByPositionFunction(effectivePositionFilter));
    }, [allPositionSortedPlayers, effectivePositionFilter]);

    // Position and projection lists are computed independently so switching tabs
    // doesn't invalidate the other list's memoized value.
    const projectionSortedPlayers: PlayerWithTeam[] = useMemo(() => {
        if (playerProjectionFilter !== undefined && playerProjectionFilter !== POPULAR_PLAYERS_FILTER) {
            return gameUtils.filterAndSortPlayersOnProjectionView(sortedEvents, allMarkets, playerProjectionFilter);
        }
        if (playerProjectionFilter === POPULAR_PLAYERS_FILTER) {
            return popularProjectionsPlayers || [];
        }
        return [];
    }, [playerProjectionFilter, sortedEvents, allMarkets, popularProjectionsPlayers]);

    const sortedPlayers = useMemo(() => {
        switch (filterType) {
            case Filters.Position:
                return positionSortedPlayers;
            case Filters.Projection:
                return projectionSortedPlayers;
            case Filters.SocialLineups:
            case Filters.Preview:
                return [];
        }
    }, [filterType, positionSortedPlayers, projectionSortedPlayers]);

    // Build a lookup map to re-attach events to players without scanning the events array per player.
    const playerEventMap = useMemo(() => {
        const map = new Map<string, EventInfo>();
        for (const event of sortedEvents) {
            for (const player of eventUtils.getAllPlayers(event)) {
                map.set(player.id, event);
            }
        }
        return map;
    }, [sortedEvents]);

    // Sorting works on players alone, but rendering needs the event attached to each player.
    const sortedPlayersWithEvents: PlayerWithTeamAndEvent[] = useMemo(() => {
        return sortedPlayers
            .map(player => {
                const event = playerEventMap.get(player.id);
                if (!event) {
                    console.warn('Player without event found:', player.lastName, '???');
                    return null;
                }
                return {
                    ...player,
                    event,
                };
            })
            .filter(Boolean);
    }, [sortedPlayers, playerEventMap]);

    return { sortedPlayers: sortedPlayersWithEvents, popularProjectionsPlayers };
}

export function useUFCGroupedPlayers(
    ufcEvents: EventInfo[] | undefined
): (CardSegmentTitleType | IndividualVersusEvent)[] {
    const sortedUfcEvents = useMemo(() => {
        const ufcEventsArray = ufcEvents || [];

        const groupedBySegment = ufcEventsArray
            .toSorted(sortEventsByDate)
            .reduce<Record<CardSegment, IndividualVersusEvent[]>>((prev, event) => {
                const cardSegment: CardSegment | undefined =
                    (findEventAttribute(event, EventAttributesType.CardSegment) as CardSegment) ?? CardSegment.Prelims;

                return {
                    ...prev,
                    [cardSegment]: [...(prev[cardSegment] ?? []), event as IndividualVersusEvent],
                };
            }, {} as Record<CardSegment, IndividualVersusEvent[]>);

        const eventsWithSegments: (CardSegmentTitleType | IndividualVersusEvent)[] = UFC_CARD_SEGMENT_ORDER.flatMap(
            cardSegment => {
                return groupedBySegment[cardSegment]?.length
                    ? [
                          {
                              // add typename property for easier type checking
                              __typename: 'CardSegmentTitle',
                              cardSegment,
                              // for each card segment show the first event date as the startDate of the card
                              startDate: groupedBySegment[cardSegment][0].date,
                              tournamentStartDate: findEventAttribute(
                                  groupedBySegment[cardSegment][0],
                                  EventAttributesType.TournamentStartDateTime
                              ),
                          },
                          //UFC events within cards should be sorted such that the last fight is first
                          ...groupedBySegment[cardSegment].sort(sortEventsByDate).reverse(),
                      ]
                    : [];
            }
        );

        return eventsWithSegments;
    }, [ufcEvents]);
    return sortedUfcEvents;
}

/**
 * Hook that computes the 'Popular' players for a given list of events
 * It will compute this list based on which player is also available in the top 10 query, trending players
 * or if the player has a special projection
 *
 * @returns popularProjectionsPlayers - list of popular players or undefined if the data is still loading
 */
export const usePopularProjectionsPlayers = (sortedEvents: EventInfo[]) => {
    const { popularPlayers, trendingPlayers, fetchingPopularPlayers, fetchingTrendingPlayers } =
        usePopularPlayersData();

    const popularProjectionsPlayers = useMemo(() => {
        if (fetchingPopularPlayers || fetchingTrendingPlayers || sortedEvents.length === 0) {
            return undefined;
        }

        const playersWithEventTimes = gameUtils.extractAllPlayersFromEvents(sortedEvents);

        const popularPlayersWithEventTimes = popularPlayers
            .map(player => {
                return playersWithEventTimes.find(p => p.id === player.id);
            })
            .filter(Boolean);

        const trendingPlayersWithEventTimes = trendingPlayers
            .map(player => {
                return playersWithEventTimes.find(p => p.id === player.id);
            })
            .filter(Boolean);

        const promoPlayersWithEventTimes = playersWithEventTimes.filter(player => {
            const playerProjections = player.projections;
            return playerProjections.some(projection => checkIfPromoProjection(projection.type));
        });

        const mergedList = [
            ...new Map(
                [...popularPlayersWithEventTimes, ...trendingPlayersWithEventTimes, ...promoPlayersWithEventTimes].map(
                    item => [item.id, item]
                )
            ).values(),
        ];

        const playersSplitByPromoProjections = mergedList.flatMap(player =>
            gameUtils.splitPlayerByProjections(player, { mode: 'popular-tab' })
        );

        return playersSplitByPromoProjections.sort(
            (p1, p2) =>
                sortPlayersByPromoPicks(p1, p2) || sortPlayersByEventStartDate(p1, p2) || sortPlayersByAwayTeam(p1, p2)
        );
    }, [fetchingPopularPlayers, fetchingTrendingPlayers, popularPlayers, sortedEvents, trendingPlayers]);

    return popularProjectionsPlayers;
};
